import AppNavState, { ISubItem } from 'volta/services/app-nav-state';
import StoreService from 'volta/services/store';
import { classNames } from 'volta/utils/style-utils';

import { action } from '@ember/object';
import RouterService from '@ember/routing/router-service';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

interface IArgs extends ISubItem {
  expanded?: boolean;
  /**
   * Defines whether the item is a child item thus not a top parent
   * The value will be used to hide the left blank icon
   * when no icon is defined to limit the left offset.
   */
  isSubItem?: boolean;
  favorites?: string[];
  accessibilityLabel?: string;
  onClick?: () => void;
  onToggleFav: (slug: string) => void;
}
export default class BvNavigationItem extends Component<IArgs> {
  // Services
  // ~~~~

  @service router!: RouterService;
  @service store!: StoreService;
  @service appNavState!: AppNavState;

  // Properties
  // ~~~~

  @tracked expanded = this.args.expanded ?? false;

  // Computed props
  // ~~~~

  get visibleSubItems() {
    return (this.args.subItems ?? []).filter((i) => !this.appNavState.checkIsHidden(i));
  }

  get childIsActive() {
    return Boolean(this.args.subItems?.some((i) => this.appNavState.checkIsActive(i)));
  }

  get isActive() {
    const { route, currentWhen, queryParams } = this.args;
    return this.appNavState.checkIsActive({ route, currentWhen, queryParams });
  }

  get isHidden() {
    const { hidden, subItems = [] } = this.args;
    return this.appNavState.checkIsHidden({ hidden, subItems });
  }

  get showExpanded() {
    return this.isActive || this.expanded || this.childIsActive;
  }

  get tabIndex() {
    return this.args.disabled ? -1 : 0;
  }

  get isFavorite() {
    return (this.args.favorites ?? []).includes(this.args.slug);
  }

  get itemClassNames() {
    const { disabled, subdued } = this.args;
    const { isActive, showExpanded, isFavorite } = this;

    return classNames([
      'bv-navigation__item',
      subdued && 'bv-navigation__item--subdued',
      disabled && 'bv-navigation__item--disabled',
      showExpanded && 'bv-navigation__item--sub-navigation-active',
      isFavorite && 'bv-navigation__item--is-favorite',
      isActive && 'bv-navigation__item--selected'
    ]);
  }

  // Actions
  // ~~~~~

  @action
  handleClick(event?: InputEvent) {
    if (!event) {
      return;
    }
    const { visibleSubItems = [] } = this;
    const { currentTarget } = event;
    const { isToggleItem, onClick } = this.args;

    if ((currentTarget as Element | null)?.getAttribute('href') === this.router.currentURL) {
      event.preventDefault();
    }

    if (visibleSubItems.length > 0 && isToggleItem) {
      event.preventDefault();
      this.expanded = !this.expanded;
    }

    onClick?.();
  }
}
